// src/components/legal/PrivacyModal.tsx
// Privacy Policy modal — content can be overridden from Firestore (siteContent/privacyPolicy).

import { useEffect, useState } from 'react';
import { doc, getDoc }           from 'firebase/firestore';
import { db }                    from '../../lib/firebase';
import { useLegalModalStore }    from '../../stores/useLegalModalStore';
import { LegalModal }            from './LegalModal';

// ── Hardcoded fallback content ────────────────────────────────────────────────
const FALLBACK_HTML = `
<h2>1. Introduction</h2>
<p>
  Welcome to <strong>START</strong> ("we," "us," or "our"). We are committed to protecting
  your personal information and your right to privacy. This Privacy Policy explains how we
  collect, use, disclose, and safeguard your information when you use our platform at
  <a href="https://startlearning.ge">startlearning.ge</a>, including any related mobile or
  desktop applications (collectively, the "Service").
</p>
<p>
  Please read this policy carefully. If you disagree with its terms, please discontinue
  use of the Service.
</p>

<hr />

<h2>2. Information We Collect</h2>
<h3>2.1 Information You Provide Directly</h3>
<ul>
  <li><strong>Account registration</strong> — name, email address, password (hashed), and optional profile photo.</li>
  <li><strong>Payment information</strong> — billing details processed by Stripe; we do not store full card numbers.</li>
  <li><strong>Communications</strong> — messages sent to us via the contact form or email.</li>
</ul>

<h3>2.2 Information Collected Automatically</h3>
<ul>
  <li><strong>Usage data</strong> — pages viewed, exercises completed, time spent, and progress data.</li>
  <li><strong>Device data</strong> — browser type, operating system, screen resolution, and language settings.</li>
  <li><strong>Log data</strong> — IP address, access timestamps, and referring URLs.</li>
  <li><strong>Cookies and similar technologies</strong> — see our <strong>Cookies Policy</strong> for details.</li>
</ul>

<h3>2.3 Information from Third Parties</h3>
<p>
  If you sign in with Google or another OAuth provider, we receive your name, email address,
  and profile photo as permitted by that provider's privacy settings.
</p>

<hr />

<h2>3. How We Use Your Information</h2>
<p>We use the information we collect to:</p>
<ul>
  <li>Create and manage your account and deliver the Service.</li>
  <li>Process payments and send transaction confirmations.</li>
  <li>Track your learning progress and personalise content recommendations.</li>
  <li>Send service-related notifications (e.g. password reset, receipt emails).</li>
  <li>Send marketing communications, where you have opted in.</li>
  <li>Analyse usage patterns to improve the platform.</li>
  <li>Detect, investigate, and prevent fraudulent or unauthorised activity.</li>
  <li>Comply with legal obligations.</li>
</ul>

<hr />

<h2>4. Legal Bases for Processing (GDPR)</h2>
<p>For users in the European Economic Area (EEA) or Georgia, we process your data under the following bases:</p>
<ul>
  <li><strong>Contract performance</strong> — to provide the Service you signed up for.</li>
  <li><strong>Legitimate interests</strong> — to improve our Service and prevent fraud.</li>
  <li><strong>Consent</strong> — for marketing emails and non-essential cookies.</li>
  <li><strong>Legal obligation</strong> — where required by applicable law.</li>
</ul>

<hr />

<h2>5. Sharing Your Information</h2>
<p>We do <strong>not</strong> sell your personal data. We may share it with:</p>
<ul>
  <li><strong>Firebase / Google Cloud</strong> — database, authentication, and hosting.</li>
  <li><strong>Stripe</strong> — payment processing.</li>
  <li><strong>Analytics providers</strong> — aggregated, anonymised usage data only.</li>
  <li><strong>Law enforcement</strong> — when required by a valid legal process.</li>
</ul>

<hr />

<h2>6. Data Retention</h2>
<p>
  We retain your account data for as long as your account is active. If you delete your
  account, we remove your personal data within <strong>30 days</strong>, except where
  retention is required for legal or tax purposes (up to 7 years for financial records).
</p>

<hr />

<h2>7. Your Rights</h2>
<p>Depending on your location, you may have the right to:</p>
<ul>
  <li>Access the personal data we hold about you.</li>
  <li>Correct inaccurate or incomplete data.</li>
  <li>Request deletion of your data ("right to be forgotten").</li>
  <li>Object to or restrict certain processing.</li>
  <li>Receive a portable copy of your data (data portability).</li>
  <li>Withdraw consent at any time (this does not affect prior processing).</li>
</ul>
<p>
  To exercise any of these rights, contact us at
  <a href="mailto:privacy@startlearning.ge">privacy@startlearning.ge</a>.
</p>

<hr />

<h2>8. Security</h2>
<p>
  We implement industry-standard security measures including TLS encryption in transit,
  bcrypt-hashed passwords, role-based access controls, and regular security audits.
  No method of transmission over the internet is 100% secure; we cannot guarantee
  absolute security.
</p>

<hr />

<h2>9. International Transfers</h2>
<p>
  Your data may be stored and processed in the United States or other countries where
  our service providers operate. By using the Service, you consent to such transfers,
  which are conducted under appropriate safeguards (Standard Contractual Clauses where
  applicable).
</p>

<hr />

<h2>10. Children's Privacy</h2>
<p>
  The Service is not directed at children under the age of 13. We do not knowingly
  collect personal data from children. If you believe a child has provided us with
  their information, please contact us and we will delete it promptly.
</p>

<hr />

<h2>11. Changes to This Policy</h2>
<p>
  We may update this Privacy Policy from time to time. We will notify you of material
  changes by posting the new policy on this page and updating the "Last updated" date.
  Continued use of the Service after changes constitutes your acceptance.
</p>

<hr />

<h2>12. Contact Us</h2>
<p>
  For privacy-related enquiries:<br />
  <strong>START Language Learning</strong><br />
  Tbilisi, Georgia<br />
  <a href="mailto:privacy@startlearning.ge">privacy@startlearning.ge</a>
</p>
`;

// ── Component ─────────────────────────────────────────────────────────────────
export function PrivacyModal() {
  const openModal = useLegalModalStore((s) => s.openModal);
  const isOpen    = openModal === 'privacy';

  const [htmlContent, setHtmlContent] = useState<string>(FALLBACK_HTML);

  useEffect(() => {
    getDoc(doc(db, 'siteContent', 'privacyPolicy'))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data?.html) setHtmlContent(data.html as string);
        }
      })
      .catch(() => {/* use fallback */});
  }, []);

  return (
    <LegalModal
      isOpen={isOpen}
      title="Privacy Policy"
      lastUpdated="1 January 2026"
      htmlContent={htmlContent}
    />
  );
}